import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const WA_GREEN = '#25d366';

interface WASettings {
  whatsapp_enabled: boolean;
  whatsapp_verify_token: string;
  whatsapp_role_agent: boolean;
  whatsapp_role_pbx: boolean;
}

interface WAPhone {
  id: number;
  display_name: string;
  phone_number_id: string;
  has_token: boolean;
  is_active: boolean;
}

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div onClick={() => !disabled && onChange(!on)} style={{
      width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
      background: on ? WA_GREEN : '#d1d5db', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      opacity: disabled ? 0.5 : 1,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: on ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: 14, color: '#111827',
  background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
};

const ROLES = [
  { key: 'whatsapp_role_agent' as const, label: 'Agent', desc: 'All Agent-role users can see the WhatsApp tab' },
  { key: 'whatsapp_role_pbx'   as const, label: 'PBX User', desc: 'All PBX User-role users can see the WhatsApp tab' },
];

const blankForm = { display_name: '', phone_number_id: '', access_token: '' };

export default function WhatsAppSettings() {
  const [settings, setSettings]     = useState<WASettings>({ whatsapp_enabled: false, whatsapp_verify_token: '', whatsapp_role_agent: false, whatsapp_role_pbx: false });
  const [phones, setPhones]         = useState<WAPhone[]>([]);
  const [msg, setMsg]               = useState('');
  const [verifyToken, setVerifyToken] = useState('');

  // Add form
  const [adding, setAdding]   = useState(false);
  const [addForm, setAddForm] = useState(blankForm);
  const [addSaving, setAddSaving] = useState(false);

  // Edit form
  const [editId, setEditId]     = useState<number | null>(null);
  const [editForm, setEditForm] = useState(blankForm);
  const [editSaving, setEditSaving] = useState(false);

  const webhookUrl = `${window.location.origin}/api/ai/webhook/whatsapp`;

  const load = useCallback(async () => {
    const [sr, pr] = await Promise.all([
      axios.get('/api/ai/settings').catch(() => null),
      axios.get('/api/ai/whatsapp/phones').catch(() => null),
    ]);
    if (sr) {
      setSettings(sr.data);
      setVerifyToken(sr.data.whatsapp_verify_token || '');
    }
    if (pr) setPhones(pr.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates: Partial<WASettings>) => {
    setSettings(s => ({ ...s, ...updates }));
    await axios.put('/api/ai/settings', updates).catch(() => {
      setSettings(s => ({ ...s, ...Object.fromEntries(Object.keys(updates).map(k => [k, (s as any)[k]])) }));
    });
  };

  const saveVerifyToken = async () => {
    try {
      await axios.put('/api/ai/settings', { whatsapp_verify_token: verifyToken });
      setMsg('Verify token saved');
    } catch { setMsg('Save failed'); }
  };

  const addPhone = async () => {
    if (!addForm.display_name.trim() || !addForm.phone_number_id.trim() || !addForm.access_token.trim()) {
      setMsg('All fields are required when adding a number');
      return;
    }
    setAddSaving(true); setMsg('');
    try {
      await axios.post('/api/ai/whatsapp/phones', {
        display_name:    addForm.display_name.trim(),
        phone_number_id: addForm.phone_number_id.trim(),
        access_token:    addForm.access_token.trim(),
        is_active:       true,
      });
      setAddForm(blankForm);
      setAdding(false);
      await load();
      setMsg('Number added successfully');
    } catch (e: any) {
      setMsg('Failed: ' + (e?.response?.data?.detail || e?.message));
    } finally { setAddSaving(false); }
  };

  const startEdit = (p: WAPhone) => {
    setEditId(p.id);
    setEditForm({ display_name: p.display_name, phone_number_id: p.phone_number_id, access_token: '' });
  };

  const saveEdit = async () => {
    if (!editId) return;
    setEditSaving(true); setMsg('');
    try {
      const body: any = {
        display_name:    editForm.display_name.trim(),
        phone_number_id: editForm.phone_number_id.trim(),
      };
      if (editForm.access_token.trim()) body.access_token = editForm.access_token.trim();
      await axios.put(`/api/ai/whatsapp/phones/${editId}`, body);
      setEditId(null);
      await load();
      setMsg('Number updated');
    } catch (e: any) {
      setMsg('Failed: ' + (e?.response?.data?.detail || e?.message));
    } finally { setEditSaving(false); }
  };

  const toggleActive = async (p: WAPhone) => {
    try {
      await axios.put(`/api/ai/whatsapp/phones/${p.id}`, { is_active: !p.is_active });
      await load();
    } catch { setMsg('Failed to update status'); }
  };

  const deletePhone = async (p: WAPhone) => {
    if (!window.confirm(`Delete "${p.display_name}"? Existing conversations on this number will remain but new messages won't be received.`)) return;
    try {
      await axios.delete(`/api/ai/whatsapp/phones/${p.id}`);
      await load();
      setMsg('Number deleted');
    } catch (e: any) {
      setMsg('Delete failed: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  const isError = msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('failed');

  return (
    <div style={{ maxWidth: 700, padding: '24px 28px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: WA_GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.509 5.838L0 24l6.335-1.484A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.728.873.936-3.629-.234-.374A9.818 9.818 0 1112 21.818z"/>
          </svg>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#111827' }}>WhatsApp Settings</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Manage phone numbers, webhook, and role access</div>
        </div>
      </div>

      {msg && (
        <div style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: isError ? '#fee2e2' : '#dcfce7', color: isError ? '#991b1b' : '#166534',
          border: `1px solid ${isError ? '#fca5a5' : '#86efac'}` }}>
          {msg}
        </div>
      )}

      {/* ── Global toggle ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Enable WhatsApp</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {settings.whatsapp_enabled ? 'WhatsApp is active — messages are being received' : 'WhatsApp is disabled globally'}
            </div>
          </div>
          <Toggle on={settings.whatsapp_enabled} onChange={v => patch({ whatsapp_enabled: v })} />
        </div>
      </div>

      {/* ── Webhook ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 16 }}>Webhook Configuration</div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
            Webhook URL <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— shared by all numbers</span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={webhookUrl} readOnly style={{ ...inp, background: '#f9fafb', color: '#6b7280', flex: 1 }} />
            <button onClick={() => { navigator.clipboard.writeText(webhookUrl); setMsg('Webhook URL copied'); }}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Copy
            </button>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
            Verify Token <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— same token used for all numbers</span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={verifyToken} onChange={e => setVerifyToken(e.target.value)} placeholder="Custom verify token" style={{ ...inp, flex: 1 }} />
            <button onClick={() => setVerifyToken(Array.from(crypto.getRandomValues(new Uint8Array(18))).map(b => b.toString(16).padStart(2, '0')).join(''))}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Generate
            </button>
            <button onClick={saveVerifyToken}
              style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: WA_GREEN, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Save
            </button>
          </div>
        </div>
      </div>

      {/* ── Role Access ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>Role Access</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Select which roles can see the WhatsApp tab in the agent portal.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ROLES.map(role => (
            <div key={role.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 10, border: '1px solid #f3f4f6', background: settings[role.key] ? '#f0fdf4' : '#fafafa' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{role.label}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{role.desc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: settings[role.key] ? '#166534' : '#9ca3af' }}>
                  {settings[role.key] ? 'Enabled' : 'Disabled'}
                </span>
                <Toggle on={settings[role.key]} onChange={v => patch({ [role.key]: v } as any)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Phone Numbers ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Phone Numbers</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{phones.length} number{phones.length !== 1 ? 's' : ''} configured</div>
          </div>
          {!adding && (
            <button onClick={() => { setAdding(true); setEditId(null); setMsg(''); }}
              style={{ background: WA_GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              + Add Number
            </button>
          )}
        </div>

        {/* Add form */}
        {adding && (
          <div style={{ border: '2px dashed #86efac', borderRadius: 10, padding: 16, marginBottom: 16, background: '#f0fdf4' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#166534', marginBottom: 12 }}>New WhatsApp Number</div>
            <div style={{ display: 'grid', gap: 10 }}>
              <input value={addForm.display_name} onChange={e => setAddForm(f => ({ ...f, display_name: e.target.value }))}
                placeholder="Display name (e.g. Support Line, Sales)" style={inp} />
              <input value={addForm.phone_number_id} onChange={e => setAddForm(f => ({ ...f, phone_number_id: e.target.value }))}
                placeholder="Phone Number ID (from Meta Developer Console)" style={inp} />
              <input type="password" value={addForm.access_token} onChange={e => setAddForm(f => ({ ...f, access_token: e.target.value }))}
                placeholder="Permanent Access Token" style={inp} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={addPhone} disabled={addSaving}
                style={{ background: WA_GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: addSaving ? 'not-allowed' : 'pointer', opacity: addSaving ? 0.7 : 1 }}>
                {addSaving ? 'Adding…' : 'Add'}
              </button>
              <button onClick={() => { setAdding(false); setAddForm(blankForm); }}
                style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Phone list */}
        {phones.length === 0 && !adding ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 14 }}>
            No phone numbers added yet. Click "Add Number" to get started.
          </div>
        ) : phones.map(p => (
          editId === p.id ? (
            /* Edit form */
            <div key={p.id} style={{ border: '2px solid #6366f1', borderRadius: 10, padding: 16, marginBottom: 10, background: '#f5f3ff' }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#4f46e5', marginBottom: 12 }}>Edit: {p.display_name}</div>
              <div style={{ display: 'grid', gap: 10 }}>
                <input value={editForm.display_name} onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                  placeholder="Display name" style={inp} />
                <input value={editForm.phone_number_id} onChange={e => setEditForm(f => ({ ...f, phone_number_id: e.target.value }))}
                  placeholder="Phone Number ID" style={inp} />
                <input type="password" value={editForm.access_token} onChange={e => setEditForm(f => ({ ...f, access_token: e.target.value }))}
                  placeholder="New access token (leave blank to keep current)" style={inp} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={saveEdit} disabled={editSaving}
                  style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: editSaving ? 'not-allowed' : 'pointer', opacity: editSaving ? 0.7 : 1 }}>
                  {editSaving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditId(null)}
                  style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Phone card */
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 10, background: p.is_active ? '#fff' : '#f9fafb' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: p.is_active ? WA_GREEN : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{p.display_name}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  ID: {p.phone_number_id}
                  {' · '}
                  {p.has_token ? <span style={{ color: '#166534' }}>Token set</span> : <span style={{ color: '#991b1b' }}>No token</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: p.is_active ? '#dcfce7' : '#f3f4f6', color: p.is_active ? '#166534' : '#6b7280' }}>
                  {p.is_active ? 'Active' : 'Inactive'}
                </span>
                <Toggle on={p.is_active} onChange={() => toggleActive(p)} />
                <button onClick={() => startEdit(p)}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                  Edit
                </button>
                <button onClick={() => deletePhone(p)}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}>
                  Delete
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
